/**
 * Unified Widget System
 * Configurable widget components that adapt based on configuration
 * No pre-built specific widgets - everything is configurable
 */

import React from 'react';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';

// Import the component libraries
import { 
  GarminCircularGauge,
  GarminRPMGauge,
  GarminSpeedGauge,
  GarminTemperatureGauge,
  GarminVoltageGauge
} from './GarminGauges';

import ProgressWidget from './ProgressWidget';

import { 
  GarminLargeDigitalDisplay, 
  GarminCompactDigitalDisplay, 
  GarminMultiValueDisplay 
} from './GarminDigitalDisplays';

// Widget Container that handles sizing
const WidgetContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: ${props => {
    switch (props.size) {
      case 'small': return '120px';
      case 'medium': return '200px';
      case 'large': return '280px';
      default: return '200px';
    }
  }};
  display: flex;
  flex-direction: column;
`;

/**
 * Universal Digital Widget
 * Configurable digital display that adapts to different formats and data
 */
export const DigitalWidget = ({ 
  config = {},
  data = {},
  size = 'medium' 
}) => {
  const {
    displayFormat = 'single', // 'single', 'multi', 'compact'
    title = 'Value',
    unit = '',
    showTrend = false,
    showStatus = true,
    values = [] // For multi-value format
  } = config;
  const precision = config.precision ?? config.decimals ?? config.decimalPlaces ?? 1;

  const widgetProps = {
    title,
    value: data.value || 0,
    unit,
    precision,
    status: data.status || 'connected',
    trend: showTrend ? data.trend : null
  };

  return (
    <WidgetContainer size={size}>
      {displayFormat === 'single' && (
        <GarminLargeDigitalDisplay {...widgetProps} />
      )}
      
      {displayFormat === 'compact' && (
        <GarminCompactDigitalDisplay {...widgetProps} />
      )}
      
      {displayFormat === 'multi' && (
        <GarminMultiValueDisplay 
          title={title}
          values={values.map(v => ({
            label: v.label,
            value: data[v.dataPath] || 0,
            unit: v.unit,
            color: v.color || theme.colors.text.primary
          }))}
        />
      )}
    </WidgetContainer>
  );
};

/**
 * Universal Gauge Widget
 * Configurable gauge that adapts to circular, linear, or compass formats
 */
export const GaugeWidget = ({ 
  config = {},
  data = {},
  size = 'medium' 
}) => {
  const {
    gaugeType = 'circular', // 'circular', 'linear', 'compass'
    min = 0,
    max = 100,
    zones = [],
    showNeedle = true,
    showDigitalOverlay = true,
    title = 'Gauge',
    unit = '',
  } = config;
  const precision = config.precision ?? config.decimals ?? config.decimalPlaces ?? 1;

  const widgetProps = {
    title,
    value: data.value || 0,
    min,
    max,
    unit,
    precision,
    zones,
    showNeedle,
    showDigitalOverlay
  };

  return (
    <WidgetContainer size={size}>
      {gaugeType === 'circular' && (
        <GarminCircularGauge {...widgetProps} />
      )}
      
      {gaugeType === 'linear' && (
        <GarminCircularGauge {...widgetProps} />
      )}
      
      {gaugeType === 'compass' && (
        <GarminCircularGauge 
          value={data.value || 0}
          title={title}
          min={0}
          max={360}
          unit="°"
        />
      )}
    </WidgetContainer>
  );
};

/**
 * Universal Bar Widget
 * Configurable bar/progress display with horizontal/vertical orientation
 */
export const BarWidget = ({ 
  config = {},
  data = {},
  size = 'medium' 
}) => {
  const {
    orientation = 'horizontal', // 'horizontal', 'vertical'
    barType = 'progress', // 'progress', 'tank', 'battery'
    min = 0,
    max = 100,
    zones = [],
    title = 'Progress',
    unit = '%',
    precision = 1,
    showScale = true
  } = config;

  const widgetProps = {
    title,
    value: data.value || 0,
    min,
    max,
    unit,
    precision,
    zones,
    showScale
  };

  return (
    <WidgetContainer size={size}>
      <ProgressWidget
        title={title}
        value={data.value || 0}
        unit={unit}
        min={min}
        max={max}
        thresholds={zones.map(zone => ({
          value: zone.min,
          color: zone.color,
          label: zone.label
        }))}
        label={title}
        showPercentage={barType === 'progress'}
        color={config.color || theme.colors.primary.main}
      />
    </WidgetContainer>
  );
};

/**
 * Universal Chart Widget
 * Configurable chart display for time-series and historical data
 */
export const ChartWidget = ({ 
  config = {},
  data = {},
  size = 'medium' 
}) => {
  const {
    chartType = 'line', // 'line', 'bar', 'area'
    timeRange = '1h',
    showGrid = true,
    showLegend = true,
    title = 'Chart'
  } = config;

  // Placeholder for chart implementation
  return (
    <WidgetContainer size={size}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        background: theme.colors.background.paper,
        borderRadius: theme.borderRadius.lg,
        border: `2px solid ${theme.colors.border.primary}`,
        color: theme.colors.text.secondary
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: theme.fontSizes.sm, marginBottom: '0.5rem' }}>
            {title}
          </div>
          <div style={{ fontSize: theme.fontSizes.xs }}>
            {chartType.toUpperCase()} Chart ({timeRange})
          </div>
          <div style={{ fontSize: theme.fontSizes.xs, marginTop: '0.25rem' }}>
            Coming Soon
          </div>
        </div>
      </div>
    </WidgetContainer>
  );
};

/**
 * Widget Type Registry
 * Maps widget types to their components and default configurations
 */
export const WIDGET_TYPES = {
  digital: {
    component: DigitalWidget,
    name: 'Digital Display',
    description: 'Numeric values with customizable formatting',
    icon: '123',
    defaultConfig: {
      displayFormat: 'single',
      precision: 1,
      showTrend: false,
      showStatus: true
    },
    configOptions: {
      displayFormat: {
        type: 'select',
        label: 'Display Format',
        options: [
          { value: 'single', label: 'Single Value' },
          { value: 'compact', label: 'Compact' },
          { value: 'multi', label: 'Multi-Value' }
        ]
      },
      precision: {
        type: 'number',
        label: 'Decimal Places',
        min: 0,
        max: 3
      },
      showTrend: {
        type: 'boolean',
        label: 'Show Trend Arrow'
      }
    }
  },
  
  gauge: {
    component: GaugeWidget,
    name: 'Gauge',
    description: 'Analog gauges with zones and thresholds',
    icon: '○',
    defaultConfig: {
      gaugeType: 'circular',
      min: 0,
      max: 100,
      showNeedle: true,
      showDigitalOverlay: true,
      zones: []
    },
    configOptions: {
      gaugeType: {
        type: 'select',
        label: 'Gauge Type',
        options: [
          { value: 'circular', label: 'Circular' },
          { value: 'linear', label: 'Linear' },
          { value: 'compass', label: 'Compass' }
        ]
      },
      min: {
        type: 'number',
        label: 'Minimum Value'
      },
      max: {
        type: 'number',
        label: 'Maximum Value'
      },
      showDigitalOverlay: {
        type: 'boolean',
        label: 'Show Digital Value'
      }
    }
  },
  
  bar: {
    component: BarWidget,
    name: 'Bar/Progress',
    description: 'Progress bars and tank level indicators',
    icon: '▬',
    defaultConfig: {
      orientation: 'horizontal',
      barType: 'progress',
      min: 0,
      max: 100,
      showScale: true
    },
    configOptions: {
      orientation: {
        type: 'select',
        label: 'Orientation',
        options: [
          { value: 'horizontal', label: 'Horizontal' },
          { value: 'vertical', label: 'Vertical' }
        ]
      },
      barType: {
        type: 'select',
        label: 'Bar Type',
        options: [
          { value: 'progress', label: 'Progress Bar' },
          { value: 'tank', label: 'Tank Level' },
          { value: 'battery', label: 'Battery Level' }
        ]
      },
      showScale: {
        type: 'boolean',
        label: 'Show Scale Marks'
      }
    }
  },
  
  chart: {
    component: ChartWidget,
    name: 'Chart',
    description: 'Time-series charts and graphs',
    icon: '📈',
    defaultConfig: {
      chartType: 'line',
      timeRange: '1h',
      showGrid: true,
      showLegend: true
    },
    configOptions: {
      chartType: {
        type: 'select',
        label: 'Chart Type',
        options: [
          { value: 'line', label: 'Line Chart' },
          { value: 'bar', label: 'Bar Chart' },
          { value: 'area', label: 'Area Chart' }
        ]
      },
      timeRange: {
        type: 'select',
        label: 'Time Range',
        options: [
          { value: '5m', label: '5 Minutes' },
          { value: '1h', label: '1 Hour' },
          { value: '6h', label: '6 Hours' },
          { value: '24h', label: '24 Hours' }
        ]
      }
    }
  },
  
  // Garmin Gauge Widgets
  'garmin-rpm': {
    component: ({ config = {}, data = {} }) => (
      <WidgetContainer size="medium">
        <GarminRPMGauge 
          value={data.value || 0} 
          max={config.max || 4000}
        />
      </WidgetContainer>
    ),
    name: 'Garmin RPM Gauge',
    description: 'Professional engine RPM gauge with Garmin styling',
    icon: '⚙️',
    category: 'garmin',
    defaultConfig: {
      max: 4000
    },
    configOptions: {
      max: {
        type: 'number',
        label: 'Max RPM',
        min: 1000,
        max: 8000
      }
    }
  },
  
  'garmin-speed': {
    component: ({ config = {}, data = {} }) => (
      <WidgetContainer size="medium">
        <GarminSpeedGauge 
          value={data.value || 0} 
          max={config.max || 25}
          unit={config.unit || 'kts'}
        />
      </WidgetContainer>
    ),
    name: 'Garmin Speed Gauge',
    description: 'Vessel speed gauge with Garmin marine styling',
    icon: '🚤',
    category: 'garmin',
    defaultConfig: {
      max: 25,
      unit: 'kts'
    },
    configOptions: {
      max: {
        type: 'number',
        label: 'Max Speed',
        min: 10,
        max: 50
      },
      unit: {
        type: 'select',
        label: 'Speed Unit',
        options: [
          { value: 'kts', label: 'Knots' },
          { value: 'mph', label: 'MPH' },
          { value: 'km/h', label: 'km/h' }
        ]
      }
    }
  },
  
  'garmin-temperature': {
    component: ({ config = {}, data = {} }) => (
      <WidgetContainer size="medium">
        <GarminTemperatureGauge 
          title={config.title || 'Temperature'}
          value={data.value || 0} 
          min={config.min || 100}
          max={config.max || 220}
          unit={config.unit || '°F'}
        />
      </WidgetContainer>
    ),
    name: 'Garmin Temperature Gauge',
    description: 'Engine or system temperature gauge',
    icon: '🌡️',
    category: 'garmin',
    defaultConfig: {
      title: 'Temperature',
      min: 100,
      max: 220,
      unit: '°F'
    },
    configOptions: {
      title: {
        type: 'text',
        label: 'Gauge Title'
      },
      min: {
        type: 'number',
        label: 'Min Temperature'
      },
      max: {
        type: 'number',
        label: 'Max Temperature'
      },
      unit: {
        type: 'select',
        label: 'Temperature Unit',
        options: [
          { value: '°F', label: 'Fahrenheit' },
          { value: '°C', label: 'Celsius' }
        ]
      }
    }
  },
  
  'garmin-voltage': {
    component: ({ config = {}, data = {} }) => (
      <WidgetContainer size="medium">
        <GarminVoltageGauge 
          value={data.value || 12.0} 
          min={config.min || 10}
          max={config.max || 15}
        />
      </WidgetContainer>
    ),
    name: 'Garmin Voltage Gauge',
    description: 'Battery voltage monitoring with Garmin styling',
    icon: '🔋',
    category: 'garmin',
    defaultConfig: {
      min: 10,
      max: 15
    },
    configOptions: {
      min: {
        type: 'number',
        label: 'Min Voltage',
        min: 8,
        max: 12
      },
      max: {
        type: 'number',
        label: 'Max Voltage',
        min: 12,
        max: 20
      }
    }
  },
  
  'garmin-circular': {
    component: ({ config = {}, data = {} }) => (
      <WidgetContainer size="medium">
        <GarminCircularGauge 
          title={config.title || 'Gauge'}
          value={data.value || 0} 
          min={config.min || 0}
          max={config.max || 100}
          unit={config.unit || ''}
        />
      </WidgetContainer>
    ),
    name: 'Garmin Circular Gauge',
    description: 'Customizable circular gauge with Garmin marine styling',
    icon: '⭕',
    category: 'garmin',
    defaultConfig: {
      title: 'Gauge',
      min: 0,
      max: 100,
      unit: ''
    },
    configOptions: {
      title: {
        type: 'text',
        label: 'Gauge Title'
      },
      min: {
        type: 'number',
        label: 'Minimum Value'
      },
      max: {
        type: 'number',
        label: 'Maximum Value'
      },
      unit: {
        type: 'text',
        label: 'Unit Label'
      }
    }
  }
};

/**
 * Universal Widget Renderer
 * Renders any widget type based on configuration
 */
export const UniversalWidget = ({ 
  type = 'digital',
  config = {},
  data = {},
  size = 'medium',
  onConfigChange = null
}) => {
  const widgetType = WIDGET_TYPES[type];
  
  if (!widgetType) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        color: theme.colors.text.secondary,
        background: theme.colors.background.paper,
        borderRadius: theme.borderRadius.lg,
        border: `2px dashed ${theme.colors.border.secondary}`
      }}>
        Unknown widget type: {type}
      </div>
    );
  }
  
  const Component = widgetType.component;
  const mergedConfig = { ...widgetType.defaultConfig, ...config };
  
  return (
    <Component 
      config={mergedConfig}
      data={data}
      size={size}
    />
  );
};

export default {
  DigitalWidget,
  GaugeWidget,
  BarWidget,
  ChartWidget,
  UniversalWidget,
  WIDGET_TYPES
};